import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { HubBlobUrlPreview } from "@/components/HubBlobUrlPreview";
import { Button } from "@/components/ui/Button";
import { HoverHintTrigger } from "@/components/ui/title-help";

import {
  fetchApInvoice,
  fetchApInvoiceDocumentById,
  fetchApPurchaseOrderVendorInvoices,
  type ApPurchaseOrderVendorInvoice,
} from "./apInvoicesApi";

function invoiceLabel(row: ApPurchaseOrderVendorInvoice): string {
  const number = row.vendor_invoice_number?.trim();
  return number != null && number !== "" ? number : `Invoice ${row.id}`;
}

function statusLabel(status: string): string {
  return status.replaceAll("_", " ");
}

function siblingNote(row: ApPurchaseOrderVendorInvoice): string | null {
  if (row.merged_into_accounts_payable_invoice_id != null) return "Merged into another invoice";
  const purchaseInvoice = row.purchase_invoice_mv_number?.trim();
  if (purchaseInvoice != null && purchaseInvoice !== "") return `Purchase invoice ${purchaseInvoice}`;
  if (!row.merge_eligible) return "Not ready to merge";
  return null;
}

function ApInvoiceSiblingDocumentPeek(props: { invoiceId: string }): JSX.Element {
  const [documentId, setDocumentId] = useState<string | null>(null);
  const invoiceQuery = useQuery({
    queryKey: ["accounting", "ap-invoice", props.invoiceId],
    queryFn: () => fetchApInvoice(props.invoiceId),
  });
  const documents = invoiceQuery.data?.documents ?? [];
  const selectedId =
    documentId != null && documents.some((document) => document.id === documentId)
      ? documentId
      : (documents[0]?.id ?? null);
  const documentQuery = useQuery({
    queryKey: ["accounting", "ap-invoice-document", props.invoiceId, selectedId],
    queryFn: () => fetchApInvoiceDocumentById(props.invoiceId, selectedId ?? ""),
    enabled: selectedId != null,
  });

  if (invoiceQuery.isLoading || (selectedId != null && documentQuery.isLoading)) {
    return <p className="text-sm text-slate-500">Loading invoice…</p>;
  }
  if (documentQuery.data?.signedUrl == null) {
    return <p className="text-sm text-slate-600">This invoice has no attachment to preview.</p>;
  }
  return (
    <div className="w-[32rem] max-w-[70vw]">
      {documents.length > 1 ? (
        <label className="mb-2 block text-sm text-slate-700">
          Attachment
          <select
            className="mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm"
            value={selectedId ?? ""}
            onChange={(event) => setDocumentId(event.target.value)}
          >
            {documents.map((document) => (
              <option key={document.id} value={document.id}>
                {document.filename?.trim() || `Document ${document.id}`}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="h-80">
        <HubBlobUrlPreview
          previewUrl={documentQuery.data.signedUrl}
          filename={documentQuery.data.filename ?? "invoice"}
          contentType={documentQuery.data.contentType}
          fillAvailableHeight
          hidePdfSidebar
        />
      </div>
    </div>
  );
}

export function ApInvoicePoSiblingInvoices(props: {
  invoiceId: string;
  purchaseOrderId: string;
  canMerge: boolean;
  mergePending: boolean;
  onMerge: (sourceInvoiceId: string) => void;
}): JSX.Element | null {
  const location = useLocation();
  const [confirmMergeId, setConfirmMergeId] = useState<string | null>(null);
  const siblingsQuery = useQuery({
    queryKey: ["accounting", "ap-po-vendor-invoices", props.purchaseOrderId, props.invoiceId],
    queryFn: () => fetchApPurchaseOrderVendorInvoices(props.purchaseOrderId, props.invoiceId),
    enabled: props.purchaseOrderId !== "",
  });
  const rows = siblingsQuery.data ?? [];

  if (siblingsQuery.isLoading) {
    return <p className="text-xs text-slate-500">Looking for other invoices on this purchase order…</p>;
  }
  if (siblingsQuery.isError) {
    return <p className="text-xs text-rose-700">Could not load other invoices on this purchase order.</p>;
  }
  if (rows.length === 0) return null;

  return (
    <div>
      <p className="text-xs font-medium text-slate-700">Other invoices on this purchase order</p>
      <ul className="mt-2 space-y-1.5">
        {rows.map((row) => {
          const note = siblingNote(row);
          const allowMerge = props.canMerge && row.merge_eligible;
          return (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5"
            >
              <div className="min-w-0">
                <HoverHintTrigger
                  block
                  closeOnTriggerActivate
                  trigger={
                    <Link
                      to={{ pathname: `/accounting/ap/${row.id}/edit`, search: location.search }}
                      className="block truncate text-sm font-medium text-sky-700 hover:text-sky-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-1"
                    >
                      {invoiceLabel(row)}
                    </Link>
                  }
                >
                  <ApInvoiceSiblingDocumentPeek invoiceId={row.id} />
                </HoverHintTrigger>
                <p className="text-xs text-slate-600">
                  {[
                    row.invoice_date,
                    row.total_amount != null ? `${row.total_amount} ${row.currency}` : null,
                    statusLabel(row.status),
                    note,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              </div>
              {allowMerge ? (
                confirmMergeId === row.id ? (
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      size="sm"
                      disabled={props.mergePending}
                      onClick={() => props.onMerge(row.id)}
                    >
                      {props.mergePending ? "Merging…" : "Confirm merge"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={props.mergePending}
                      onClick={() => setConfirmMergeId(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="shrink-0"
                    disabled={props.mergePending}
                    onClick={() => setConfirmMergeId(row.id)}
                  >
                    Merge
                  </Button>
                )
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
